<template>
  <button
    v-if="!bangbotEntryCollapsed"
    class="bangbot-entry"
    type="button"
    :class="{
      'is-ready': bangbotEntryReady,
      'is-dragging': bangbotDrag.active,
    }"
    :style="bangbotEntryStyle"
    title="打开 BangBot"
    aria-label="打开 BangBot"
    @pointerdown="startBangbotDrag"
    @dragstart.prevent
    @click="openBangbot"
  >
    <span class="bangbot-entry__badge"></span>
    <span class="bangbot-entry__icon">
      <svg-icon icon-class="bangbot"></svg-icon>
    </span>
    <span class="bangbot-entry__bubble" :class="bangbotBubbleSideClass">
      来问我吧~
    </span>
  </button>

  <button
    v-else
    class="bangbot-entry-tab"
    type="button"
    :class="`is-${bangbotEntryCollapsedSide}`"
    :style="bangbotEntryTabStyle"
    :title="
      bangbotEntryCollapsedSide === 'left'
        ? '从左侧展开 BangBot'
        : '从右侧展开 BangBot'
    "
    :aria-label="
      bangbotEntryCollapsedSide === 'left'
        ? '从左侧展开 BangBot'
        : '从右侧展开 BangBot'
    "
    @click="restoreBangbotEntry"
  >
    <el-icon>
      <DArrowRight v-if="bangbotEntryCollapsedSide === 'left'" />
      <DArrowLeft v-else />
    </el-icon>
  </button>
</template>

<script>
import { DArrowLeft, DArrowRight } from "@element-plus/icons-vue";
import { appsData, dynamicRegisterApps } from "@/utils/registerMicroAppsConfig";
import SvgIcon from "@/components/svgIcon/SvgIcon.vue";

export default {
  name: "BangbotEntry",
  components: {
    SvgIcon,
    DArrowLeft,
    DArrowRight,
  },
  computed: {
    bangbotEntryStyle() {
      return {
        transform: `translate3d(${this.bangbotEntryPosition.x}px, ${this.bangbotEntryPosition.y}px, 0)`,
      };
    },
    bangbotEntryTabStyle() {
      return {
        top: `${this.bangbotEntryPosition.y}px`,
      };
    },
    bangbotBubbleSideClass() {
      return this.bangbotEntryPosition.x > window.innerWidth / 2
        ? "is-left"
        : "is-right";
    },
  },
  data() {
    return {
      bangbotEntryReady: false,
      bangbotEntryPosition: {
        x: 0,
        y: 0,
      },
      bangbotDrag: {
        active: false,
        moved: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0,
      },
      bangbotSuppressClick: false,
      bangbotSuppressTimer: null,
      bangbotEntryCollapsed: false,
      bangbotEntryCollapsedSide: "right",
    };
  },
  mounted() {
    this.initBangbotEntry();
    window.addEventListener("resize", this.handleBangbotResize);
  },
  beforeUnmount() {
    this.teardownBangbotDrag();
    window.removeEventListener("resize", this.handleBangbotResize);
    if (this.bangbotSuppressTimer) {
      window.clearTimeout(this.bangbotSuppressTimer);
      this.bangbotSuppressTimer = null;
    }
  },
  methods: {
    getBangbotEntryMetrics() {
      return {
        width: 92,
        height: 104,
        gap: 24,
        minTop: 120,
      };
    },
    getBangbotEntryTabMetrics() {
      return {
        width: 30,
        height: 76,
      };
    },
    clampBangbotValue(value, min, max) {
      return Math.min(Math.max(value, min), max);
    },
    getBangbotVerticalBounds(height) {
      const { gap, minTop } = this.getBangbotEntryMetrics();
      return {
        minY: minTop,
        maxY: Math.max(minTop, window.innerHeight - height - gap),
      };
    },
    getSavedBangbotPosition() {
      try {
        const raw = localStorage.getItem("bangbot-float-position");
        if (!raw) {
          return null;
        }

        const parsed = JSON.parse(raw);
        if (
          typeof parsed?.x === "number" &&
          Number.isFinite(parsed.x) &&
          typeof parsed?.y === "number" &&
          Number.isFinite(parsed.y)
        ) {
          return {
            x: parsed.x,
            y: parsed.y,
            collapsed: Boolean(parsed.collapsed),
            side: parsed.side === "left" ? "left" : "right",
          };
        }
      } catch (error) {
        console.error("读取 BangBot 入口位置失败:", error);
      }

      return null;
    },
    saveBangbotPosition() {
      try {
        localStorage.setItem(
          "bangbot-float-position",
          JSON.stringify({
            ...this.bangbotEntryPosition,
            collapsed: this.bangbotEntryCollapsed,
            side: this.bangbotEntryCollapsedSide,
          })
        );
      } catch (error) {
        console.error("保存 BangBot 入口位置失败:", error);
      }
    },
    getNormalizedBangbotPosition(position = {}) {
      const { width, height, gap, minTop } = this.getBangbotEntryMetrics();
      const maxX = Math.max(gap, window.innerWidth - width - gap);
      const maxY = Math.max(minTop, window.innerHeight - height - gap);
      const minX = gap;
      const minY = minTop;

      return {
        x: this.clampBangbotValue(Number(position.x || 0), minX, maxX),
        y: this.clampBangbotValue(Number(position.y || 0), minY, maxY),
      };
    },
    getNormalizedBangbotTabPosition(position = {}) {
      const { height } = this.getBangbotEntryTabMetrics();
      const { minY, maxY } = this.getBangbotVerticalBounds(height);

      return {
        y: this.clampBangbotValue(Number(position.y || 0), minY, maxY),
      };
    },
    initBangbotEntry() {
      const { width, gap, minTop } = this.getBangbotEntryMetrics();
      const defaultPosition = {
        x: Math.max(gap, window.innerWidth - width - 32),
        y: Math.max(minTop, 150),
      };
      const savedPosition = this.getSavedBangbotPosition();
      const nextPosition = savedPosition || defaultPosition;

      this.bangbotEntryCollapsed = Boolean(savedPosition?.collapsed);
      this.bangbotEntryCollapsedSide = savedPosition?.side || "right";
      this.bangbotEntryPosition = this.bangbotEntryCollapsed
        ? {
            ...defaultPosition,
            ...this.getNormalizedBangbotTabPosition(nextPosition),
          }
        : this.getNormalizedBangbotPosition(nextPosition);
      this.bangbotEntryReady = true;
      this.saveBangbotPosition();
    },
    handleBangbotResize() {
      this.bangbotEntryPosition = this.bangbotEntryCollapsed
        ? {
            ...this.bangbotEntryPosition,
            ...this.getNormalizedBangbotTabPosition(this.bangbotEntryPosition),
          }
        : this.getNormalizedBangbotPosition(this.bangbotEntryPosition);
      this.saveBangbotPosition();
    },
    startBangbotDrag(event) {
      if (event.pointerType === "mouse" && event.button !== 0) {
        return;
      }

      this.teardownBangbotDrag();
      if (event.currentTarget?.setPointerCapture) {
        event.currentTarget.setPointerCapture(event.pointerId);
      }

      this.bangbotDrag = {
        active: true,
        moved: false,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: this.bangbotEntryPosition.x,
        originY: this.bangbotEntryPosition.y,
      };

      document.addEventListener("pointermove", this.handleBangbotDrag);
      document.addEventListener("pointerup", this.stopBangbotDrag);
      document.addEventListener("pointercancel", this.stopBangbotDrag);
    },
    handleBangbotDrag(event) {
      if (!this.bangbotDrag.active) {
        return;
      }
      if (event.pointerId !== this.bangbotDrag.pointerId) {
        return;
      }

      event.preventDefault();

      const deltaX = event.clientX - this.bangbotDrag.startX;
      const deltaY = event.clientY - this.bangbotDrag.startY;

      if (!this.bangbotDrag.moved) {
        const distance = Math.abs(deltaX) + Math.abs(deltaY);
        if (distance > 4) {
          this.bangbotDrag.moved = true;
        }
      }

      this.bangbotEntryPosition = {
        x: this.bangbotDrag.originX + deltaX,
        y: this.bangbotDrag.originY + deltaY,
      };
    },
    stopBangbotDrag(event) {
      if (!this.bangbotDrag.active) {
        return;
      }
      if (event?.pointerId !== this.bangbotDrag.pointerId) {
        return;
      }

      const { width } = this.getBangbotEntryMetrics();
      const { x, y } = this.bangbotEntryPosition;
      const collapseThreshold = width * 0.35;
      const collapseLeft = x <= -collapseThreshold;
      const collapseRight = x >= window.innerWidth - width + collapseThreshold;

      if (collapseLeft || collapseRight) {
        this.bangbotEntryCollapsed = true;
        this.bangbotEntryCollapsedSide = collapseLeft ? "left" : "right";
        this.bangbotEntryPosition = {
          ...this.bangbotEntryPosition,
          ...this.getNormalizedBangbotTabPosition({ y }),
        };
      } else {
        this.bangbotEntryCollapsed = false;
        this.bangbotEntryPosition = this.getNormalizedBangbotPosition(
          this.bangbotEntryPosition
        );
      }
      this.saveBangbotPosition();

      if (this.bangbotDrag.moved) {
        this.bangbotSuppressClick = true;
        if (this.bangbotSuppressTimer) {
          window.clearTimeout(this.bangbotSuppressTimer);
        }
        this.bangbotSuppressTimer = window.setTimeout(() => {
          this.bangbotSuppressClick = false;
          this.bangbotSuppressTimer = null;
        }, 160);
      }

      this.teardownBangbotDrag();
    },
    teardownBangbotDrag() {
      document.removeEventListener("pointermove", this.handleBangbotDrag);
      document.removeEventListener("pointerup", this.stopBangbotDrag);
      document.removeEventListener("pointercancel", this.stopBangbotDrag);
      this.bangbotDrag = {
        active: false,
        moved: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        originX: this.bangbotEntryPosition.x,
        originY: this.bangbotEntryPosition.y,
      };
    },
    restoreBangbotEntry() {
      const { width, gap } = this.getBangbotEntryMetrics();
      const restoreX =
        this.bangbotEntryCollapsedSide === "left"
          ? gap
          : Math.max(gap, window.innerWidth - width - gap);

      this.bangbotEntryCollapsed = false;
      this.bangbotEntryPosition = this.getNormalizedBangbotPosition({
        x: restoreX,
        y: this.bangbotEntryPosition.y,
      });
      this.saveBangbotPosition();
    },
    openBangbot() {
      if (this.bangbotSuppressClick) {
        return;
      }

      const bangbotApps = appsData.filter(
        (item) => item.activeRule === "/bangbotUi"
      );
      dynamicRegisterApps(bangbotApps);
      sessionStorage.setItem(
        "bangbot:return-path",
        this.$route.fullPath || "/app"
      );
      this.$router.push("/bangbotUi/chat");
    },
  },
};
</script>

<style lang="less" scoped>
.bangbot-entry {
  width: 92px;
  height: 104px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  box-shadow: none;
  cursor: grab;
  user-select: none;
  touch-action: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
  opacity: 0;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;

  &.is-ready {
    opacity: 1;
  }

  &.is-dragging {
    cursor: grabbing;
    transition: none;
  }
}

.bangbot-entry__badge {
  width: 14px;
  height: 14px;
  position: absolute;
  top: 0;
  left: 50%;
  z-index: 2;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: #41af0a;
  box-shadow: 0 6px 12px rgba(65, 175, 10, 0.28);
  transform: translateX(-50%);
}

.bangbot-entry__icon,
.bangbot-entry__bubble {
  position: relative;
  z-index: 1;
  pointer-events: none;
}

.bangbot-entry__icon {
  width: 92px;
  height: 104px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  .svg-icon {
    width: 92px;
    height: 104px;
    filter: drop-shadow(0 12px 24px rgba(33, 70, 232, 0.22));
    transition: transform 0.2s ease;
  }
}

.bangbot-entry__bubble {
  min-width: 132px;
  position: absolute;
  top: 16px;
  padding: 12px 22px;
  border-radius: 20px;
  background: #dfe5ff;
  font-family: Microsoft YaHei, Microsoft YaHei;
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  color: #3b4351;
  white-space: nowrap;
  opacity: 0;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.bangbot-entry__bubble.is-right {
  left: calc(100% - 4px);
  transform: translateX(-8px);
}

.bangbot-entry__bubble.is-left {
  right: calc(100% - 4px);
  transform: translateX(8px);
}

.bangbot-entry:hover .bangbot-entry__bubble,
.bangbot-entry:focus-visible .bangbot-entry__bubble {
  opacity: 1;
}

.bangbot-entry:hover .bangbot-entry__icon .svg-icon,
.bangbot-entry:focus-visible .bangbot-entry__icon .svg-icon {
  transform: translateY(-2px) scale(1.02);
}

.bangbot-entry:hover .bangbot-entry__bubble.is-right,
.bangbot-entry:focus-visible .bangbot-entry__bubble.is-right,
.bangbot-entry:hover .bangbot-entry__bubble.is-left,
.bangbot-entry:focus-visible .bangbot-entry__bubble.is-left {
  transform: translateX(0);
}

.bangbot-entry.is-dragging .bangbot-entry__bubble {
  opacity: 0 !important;
}

.bangbot-entry-tab {
  width: 30px;
  height: 76px;
  position: fixed;
  z-index: 120;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: linear-gradient(180deg, #5e84ff 0%, #3157f6 100%);
  color: #ffffff;
  cursor: pointer;
  box-shadow: 0 10px 24px rgba(33, 70, 232, 0.22);
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;

  &.is-left {
    left: 0;
    border-radius: 0 18px 18px 0;
  }

  &.is-right {
    right: 0;
    border-radius: 18px 0 0 18px;
  }

  &:hover {
    transform: scale(1.04);
  }

  .el-icon {
    font-size: 18px;
    font-weight: bold;
  }
}
</style>
